import { useEffect, useState } from 'react';
import { VideoContent } from '../types/content';
import { liveVideoService } from '../services/lives';
import { BaseSearchParams } from '../types/search';
import { ApiResponse, PaginatedResponse } from '../types/api';

interface UseUpcomingLivesOptions extends Partial<BaseSearchParams> {
  enabled?: boolean;
}

interface UseUpcomingLivesResult {
  videos: VideoContent[];
  loading: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
}

export const useUpcomingLives = ({
  limit = 5,
  enabled = true,
  sortBy = 'scheduledStartTime',
  sortType = 'asc'
}: UseUpcomingLivesOptions = {}): UseUpcomingLivesResult => {
  const [videos, setVideos] = useState<VideoContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const fetchVideos = async (pageNumber: number, replace = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await liveVideoService.searchLives({
        page: pageNumber,
        limit,
        sortBy,
        sortType,
        broadcastStatus: 'upcoming'
      });

      if (!response.data) {
        throw new Error('No data received from API');
      }

      const paginatedData = response.data as PaginatedResponse<VideoContent>;
      const newVideos = paginatedData.results;
      
      setVideos(prev => replace ? newVideos : [...prev, ...newVideos]);
      setHasMore(newVideos.length === limit);
      setPage(pageNumber);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch upcoming live videos'));
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!loading && hasMore) {
      await fetchVideos(page + 1);
    }
  };

  const refetch = async () => {
    await fetchVideos(1, true);
  };

  useEffect(() => {
    if (enabled) {
      refetch();
    }
  }, [enabled, sortBy, sortType]);

  return {
    videos,
    loading,
    error,
    hasMore,
    loadMore,
    refetch
  };
}; 